import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { toast } from "react-toastify";

export default function NewsletterSubscription() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email or phone number");
      return;
    }

    // Basic validation
    const isEmail = email.includes("@");
    const isPhone = /^[\d\s\-\+\(\)]+$/.test(email);

    if (!isEmail && !isPhone) {
      toast.error("Please enter a valid email or phone number");
      return;
    }

    setIsSubmitting(true);
    
    // TODO: Integrate with actual subscription API
    setTimeout(() => {
      toast.success("Successfully subscribed! Check your inbox for updates.");
      setEmail("");
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section className="px-4 py-12 bg-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="overflow-hidden bg-white rounded-2xl shadow-xl lg:grid lg:grid-cols-2">
          {/* Left Side - Cityscape Image */}
          <div className="relative overflow-hidden h-64 bg-gradient-to-br from-gray-900 to-gray-800 lg:h-auto">
            <img
              src="https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1200&q=80"
              alt="Modern cityscape at night"
              className="object-cover w-full h-full"
            />
            {/* Gradient overlay for better text contrast if needed */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />
          </div>

          {/* Right Side - Subscription Form */}
          <div className="flex flex-col justify-center px-6 py-12 bg-gray-100 sm:px-10 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Heading */}
              <h2 className="mb-6 text-2xl font-bold leading-tight text-center text-gray-900 sm:text-3xl">
                Get special tour packages, offers, and more
              </h2>

              {/* Subscription Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {email.includes("@") ? (
                        <Mail size={20} />
                      ) : (
                        <Phone size={20} />
                      )}
                    </div>
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your phone number or email"
                      className="w-full py-3 pl-11 pr-4 text-sm bg-white border border-gray-300 rounded-lg outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-3 text-sm font-semibold text-white bg-orange-500 rounded-lg shadow-md transition-all hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Subscribing...
                      </span>
                    ) : (
                      "Subscribe"
                    )}
                  </motion.button>
                </div>
              </form>

              {/* Optional: Additional info */}
              <p className="mt-4 text-xs text-center text-gray-500">
                By subscribing, you agree to receive updates and promotional offers
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

